import { useState } from 'react';
import { Check, UserX, X } from 'lucide-react';
import { FormDialog } from '@/components/dialog/FormDialog';
import { CheckboxField } from '@/components/form/CheckboxField';
import { SignaturePad } from '@/components/form/SignaturePad';
import { TextareaField } from '@/components/form/TextareaField';
import { useSubmit } from '@/hooks/useSubmit';
import { formatMinutes } from '@/lib/duration';
import { getApiErrorMessage } from '@/lib/apiError';
import { memberUrn } from '@/models/entities/MemberModel';
import { periodUrn } from '@/models/entities/PeriodModel';
import { timeSheetUrn } from '@/models/entities/TimeSheetModel';
import { TIME_SHEET_SIGNATURE_TYPE, TIME_SHEET_STATUS } from '@/models/references/references';
import TimeSheetAbsenceModel from '@/models/services/TimeSheetAbsenceModel';
import TimeSheetStatusHistoryModel from '@/models/services/TimeSheetStatusHistoryModel';
import { resolveReference } from '@/stores/useReferenceOptions';
import { SignDialog } from './SignDialog';
import { signTimeSheet } from './signTimeSheet';

/** Dialogues de la Validation (Dirigeant, manager) : valider, refuser avec un motif, déclarer une absence */

/** Ce qu'il faut d'une feuille pour la nommer dans un dialogue (et créer l'absence d'un employé qui n'en a pas) */
export interface SheetSummary {
  /** URN de la feuille (écritures) ; null : l'employé n'a pas encore de feuille (absence seulement) */
  urn: string | null;
  memberId: number;
  periodId: number;
  fullName: string;
  monthLabel: string;
  totalMinutes: number;
  /** Absence : son refus l'annule (feuille normale en brouillon vide) */
  isAbsence: boolean;
  /** Validation : signature du Dirigeant (définitive) ou du manager (passage au Dirigeant) */
  signAs: typeof TIME_SHEET_SIGNATURE_TYPE.DIRECTOR | typeof TIME_SHEET_SIGNATURE_TYPE.MANAGER;
}

/** Refus : seul statut posé à la main dans l'historique (soumission et validation se font en signant) */
async function reject(sheet: SheetSummary, comment: string) {
  const status = await resolveReference('timeSheetStatuses', TIME_SHEET_STATUS.REJECTED);
  await new TimeSheetStatusHistoryModel().save(
    { timeSheet: sheet.urn!, timeSheetStatus: status.urn, comment },
    { silentError: true },
  );
}

/**
 * Validation en signant : par le Dirigeant, définitive (§2.5 ; la période se clôture d'elle-même quand tout est validé) ;
 * par le manager, la feuille passe au Dirigeant
 */
export function ValidateDialog({
  sheet,
  onDone,
  onClose,
}: {
  sheet: SheetSummary;
  onDone: () => void;
  onClose: () => void;
}) {
  const byManager = sheet.signAs === TIME_SHEET_SIGNATURE_TYPE.MANAGER;

  return (
    <SignDialog
      title="Valider la feuille"
      description={
        <>
          Valider la feuille de <strong className="text-slate-900">{sheet.fullName}</strong> pour{' '}
          {sheet.monthLabel.toLowerCase()} ({formatMinutes(sheet.totalMinutes)}) ?{' '}
          {byManager ? 'Elle passera au Dirigeant pour la validation définitive.' : 'La validation est définitive.'}
        </>
      }
      confirmLabel="Valider"
      confirmIcon={Check}
      onSign={(image) => signTimeSheet(sheet.urn!, sheet.signAs, image)}
      onDone={onDone}
      onClose={onClose}
    />
  );
}

/**
 * Refus avec un motif obligatoire, montré à l'employé : sa feuille repart en brouillon pour correction. Pour une absence,
 * c'est son annulation (déclarée par erreur) : la feuille redevient normale, en brouillon vide
 */
export function RejectDialog({
  sheet,
  onDone,
  onClose,
}: {
  sheet: SheetSummary;
  onDone: () => void;
  onClose: () => void;
}) {
  const [comment, setComment] = useState('');
  const { submit, submitting, error } = useSubmit();

  return (
    <FormDialog
      title={sheet.isAbsence ? "Annuler l'absence" : 'Refuser la feuille'}
      description={
        sheet.isAbsence ? (
          <>
            L'absence de <strong className="text-slate-900">{sheet.fullName}</strong> pour{' '}
            {sheet.monthLabel.toLowerCase()} sera annulée : sa feuille repartira en brouillon, vide, pour qu'il déclare
            ses heures (l'absence pourra être déclarée de nouveau).
          </>
        ) : (
          <>
            La feuille de <strong className="text-slate-900">{sheet.fullName}</strong> pour{' '}
            {sheet.monthLabel.toLowerCase()} repartira en brouillon : l'employé pourra la corriger et la soumettre de
            nouveau.
          </>
        )
      }
      submitLabel={sheet.isAbsence ? "Annuler l'absence" : 'Refuser'}
      submitIcon={X}
      destructive
      canSubmit={!!comment.trim()}
      submitting={submitting}
      error={error}
      onSubmit={() =>
        submit(async () => {
          await reject(sheet, comment.trim());
          onDone();
        })
      }
      onClose={onClose}
    >
      <TextareaField
        id="reject-comment"
        label={sheet.isAbsence ? "Motif de l'annulation" : 'Motif du refus'}
        required
        hint="Montré à l'employé avec sa feuille."
        rows={3}
        maxLength={1000}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        autoFocus
      />
    </FormDialog>
  );
}

/**
 * Absence d'un employé qui n'a pas soumis : une feuille d'absence, sans ligne, soumise au nom du déclarant puis en
 * validation (POST /time_sheets/absence : créée, ou à la place de son brouillon, dont les lignes sont supprimées).
 * « Signer tout de suite » : le déclarant la signe ensuite, avec sa signature dessinée, comme toute validation
 * (signTimeSheet : en Dirigeant, validée ; en Manager, elle passe au Dirigeant). Deux requêtes : une signature refusée
 * laisse l'absence déclarée, en attente de validation (onDone avec l'erreur : le dialogue se ferme quand même)
 */
export function AbsenceDialog({
  sheet,
  onDone,
  onClose,
}: {
  sheet: SheetSummary;
  onDone: (signed: boolean, signError?: string) => void;
  onClose: () => void;
}) {
  const [sign, setSign] = useState(false);
  const [image, setImage] = useState<Blob | null>(null);
  const { submit, submitting, error } = useSubmit();
  const hasLines = sheet.totalMinutes > 0;
  const byManager = sheet.signAs === TIME_SHEET_SIGNATURE_TYPE.MANAGER;

  return (
    <FormDialog
      title="Déclarer une absence"
      description={
        <>
          Déclarer une absence de <strong className="text-slate-900">{sheet.fullName}</strong> pour{' '}
          {sheet.monthLabel.toLowerCase()} : sa feuille partira en validation.
          {hasLines && ' Les heures déjà saisies sur son brouillon seront supprimées.'}
        </>
      }
      submitLabel="Déclarer l'absence"
      submitIcon={UserX}
      destructive={hasLines}
      canSubmit={!sign || !!image}
      submitting={submitting}
      error={error}
      onSubmit={() =>
        submit(async () => {
          const { model } = await new TimeSheetAbsenceModel().save(
            { member: memberUrn(sheet.memberId), period: periodUrn(sheet.periodId) },
            { silentError: true },
          );
          if (!sign) return onDone(false);
          try {
            await signTimeSheet(timeSheetUrn(model.id), sheet.signAs, image!);
          } catch (e) {
            return onDone(false, getApiErrorMessage(e));
          }
          onDone(true);
        })
      }
      onClose={onClose}
    >
      <CheckboxField
        id="absence-sign"
        label="Signer tout de suite"
        hint={
          byManager
            ? 'Vous la validez en la signant : elle passe au Dirigeant.'
            : 'Vous la validez en la signant : la validation est définitive.'
        }
        checked={sign}
        onChange={(checked) => {
          setSign(checked);
          setImage(null);
        }}
      />
      {sign && <SignaturePad id="absence-signature" onChange={setImage} />}
    </FormDialog>
  );
}
